import DataTable from 'components/common/DataTable';
import Loading from 'components/common/Loading';
import Modal from 'components/common/Modal';
import useGetHypoNoeApiRequestDetails from 'hooks/hypoNoeApiRequest/useGetHypoNoeApiRequest';
import { cloneDeep, flattenDeep, get } from 'lodash';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { formatDate } from 'utils/helpers';
import type { THypoNoeApiRequestDetails } from 'hooks/hypoNoeApiRequest/useGetHypoNoeApiRequest';
import isRecord from 'utils/isRecord';
import requireValue from 'utils/requireValue';
import s from './APIRequestDetailsModal.module.scss';

type TFormatMessage = IntlShape['formatMessage'];

/** A `{ parameter, value }` row of a details table (the other fields of the parameter are kept). */
interface IParameterRow extends Record<string, unknown> {
  /** The parameter name (a String! in the schema). */
  parameter: string;
  value: string;
  highlighted?: boolean;
}

/** Adds the row of a `{ parameter, value }` object; other values (the __typename, the customers list) add none. */
function extractRows(acc: IParameterRow[], queryParameter: unknown): IParameterRow[] {
  // Reading `queryParameter.parameter` throws for null and undefined.
  const queryValue = requireValue(queryParameter, 'parameter');
  if (!isRecord(queryValue) || !queryValue.parameter) return acc;
  const { parameter, value, ...rest } = queryValue;
  let v = value;
  if (parameter === 'DateOfBirth') v = formatDate(v, 'do MMMM yyyy');
  return [...acc, { parameter: String(parameter), value: `${v}`, ...rest }];
}

const emptyRow: IParameterRow = { parameter: 'Partner', value: '', highlighted: true };

interface ICustomerDetailsTableProps {
  customers: unknown;
  t: TFormatMessage;
}

/** The customers of the request: a list of records of `{ parameter, value }` objects. */
const customerRecordsOf = (customers: unknown): Record<string, unknown>[] => {
  // `customers.map(...)` throws for anything but an array of objects, as it did.
  const list = requireValue(customers, 'map');
  if (!Array.isArray(list)) throw new TypeError('customers.map is not a function');
  return list.map((customer: unknown) => {
    if (!isRecord(customer)) throw new TypeError("Cannot create property 'emptyRow' on a customer");
    return customer;
  });
};

const CustomerDetailsTable = ({ customers, t }: ICustomerDetailsTableProps) => {
  const fields = useMemo(
    () => [
      { key: 'parameter', label: t({ id: 'hypo_noe_api_request.parameter' }) },
      { key: 'value', label: t({ id: 'hypo_noe_api_request.value' }) },
    ],
    [t],
  );

  const items = useMemo(
    () =>
      flattenDeep(
        customerRecordsOf(customers).map((_customer, idx, list) => {
          const customer = cloneDeep(_customer);
          if (idx !== list.length - 1) customer.emptyRow = emptyRow;
          const noRows: IParameterRow[] = [];
          return Object.values(customer).reduce(extractRows, noRows);
        }),
      ),
    [customers],
  );

  return (
    <>
      <h3>{t({ id: 'hypo_noe_api_request.customers' })}</h3>
      <DataTable
        items={items}
        fields={fields}
        scopedSlots={{
          parameter: ({ parameter, highlighted }: IParameterRow) => {
            return <td className={highlighted ? s.highlightedRow : s.row}>{parameter}</td>;
          },
          value: ({ value, highlighted }: IParameterRow) => {
            return <td className={highlighted ? s.highlightedRow : s.row}>{value}</td>;
          },
        }}
      />
    </>
  );
};

interface IRequestDetailsTableProps {
  requestDetails: THypoNoeApiRequestDetails | null;
}

const RequestDetailsTable = ({ requestDetails }: IRequestDetailsTableProps) => {
  const { formatMessage: t } = useIntl();
  const customers = get(requestDetails, 'request_params.customers', []);

  const fields = useMemo(
    () => [
      { key: 'parameter', label: t({ id: 'hypo_noe_api_request.parameter' }) },
      { key: 'value', label: t({ id: 'hypo_noe_api_request.value' }) },
    ],
    [t],
  );

  const items = useMemo(() => {
    const noRows: IParameterRow[] = [];
    return Object.values(requireValue(requestDetails, 'request_params').request_params).reduce(
      extractRows,
      noRows,
    );
  }, [requestDetails]);

  return (
    <div>
      <DataTable
        items={items}
        fields={fields}
        scopedSlots={{
          parameter: ({ parameter }: IParameterRow) => <td className={s.row}>{parameter}</td>,
          value: ({ value }: IParameterRow) => <td className={s.row}>{value}</td>,
        }}
      />
      <CustomerDetailsTable {...{ customers, t }} />
    </div>
  );
};

const RequestDetails = ({ requestId }: { requestId: string }) => {
  const [requestDetails, loading] = useGetHypoNoeApiRequestDetails(requestId);

  if (loading) return <Loading size={30} />;

  return <RequestDetailsTable {...{ requestDetails }} />;
};

interface IAPIRequestDetailsModalProps {
  requestId: unknown;
  isVisible: boolean;
  hideModal: () => void;
}

const APIRequestDetailsModal = ({
  requestId,
  isVisible,
  hideModal,
}: IAPIRequestDetailsModalProps) => {
  const { formatMessage: t } = useIntl();
  // The modal props hold the id string of the clicked request, or nothing while the modal is closed.
  if (typeof requestId !== 'string' || !requestId) return null;

  return (
    <Modal
      header={t({ id: 'page_titles.hypo_noe_api_requests_report' })}
      isVisible={isVisible}
      close={hideModal}
    >
      <RequestDetails {...{ requestId }} />
    </Modal>
  );
};

export default APIRequestDetailsModal;
